import { useRef, useState, useEffect } from 'react';

// Section 5 — Show Adaptation. The concrete-proof flow: what happens when a
// learner misses a concept. Voice is human-observational throughout (notices,
// spots, changes, tried, confirms) — no architectural language.
const STAGES = [
  { icon: '？', title: 'Miss a concept',     caption: 'You answer a practice question incorrectly.' },
  { icon: '◉',  title: 'AdaptOS notices',    caption: 'The system spots the specific misconception — not just that you got it wrong.' },
  { icon: '↻',  title: 'Changes angles',     caption: 'The next explanation comes from a different angle — contrast pair, worked example, or fresh framing.' },
  { icon: '✎',  title: 'Targeted practice',  caption: 'You try the concept again — but on a task designed around exactly what tripped you up.' },
  { icon: '✓',  title: 'Confirms repair',    caption: 'AdaptOS confirms the misunderstanding is actually repaired before letting you advance.' },
];

export default function AdaptationFlowSection() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="af-sec" ref={ref}>
      <div className="af-inner">
        <h2 className="af-h2">When you struggle, AdaptOS responds.</h2>
        <p className="af-sub">
          Real adaptation, not buzzwords. Here&rsquo;s what happens when a learner
          misses a concept.
        </p>

        <div className={`af-flow${inView ? ' in' : ''}`}>
          {STAGES.map((s, i) => (
            <div className="af-stage-wrap" key={s.title} style={{ transitionDelay: `${i * 0.12}s` }}>
              <div className="af-stage" data-last={s.title === 'Confirms repair' ? 'true' : 'false'}>
                <div className="af-ico" aria-hidden="true">{s.icon}</div>
                <h3 className="af-stage-title">{s.title}</h3>
                <p className="af-stage-cap">{s.caption}</p>
              </div>
              {i < STAGES.length - 1 && <div className="af-arrow" aria-hidden="true">›</div>}
            </div>
          ))}
        </div>

        <p className="af-closing">Other systems explain. AdaptOS responds.</p>
      </div>

      <style>{`
        .af-sec { position: relative; z-index: 1; padding: clamp(48px, 8vh, 96px) clamp(16px, 4vw, 40px); }
        .af-inner { max-width: 1160px; margin: 0 auto; }
        .af-h2 {
          font-family: 'Fraunces', serif; font-weight: 500;
          font-size: clamp(28px, 4vw, 44px); line-height: 1.1;
          letter-spacing: -0.02em; margin: 0; text-align: center;
        }
        .af-sub {
          font-weight: 300; color: rgba(17,17,16,0.62);
          font-size: clamp(16px, 2vw, 20px); line-height: 1.5;
          margin: 18px auto 0; max-width: 56ch; text-align: center;
        }
        .af-flow {
          margin-top: clamp(36px, 5vw, 56px);
          display: flex; align-items: stretch; justify-content: center; gap: 0;
        }
        .af-stage-wrap {
          display: flex; align-items: center; flex: 1; min-width: 0;
          opacity: 0; transform: translateY(16px);
          transition: opacity .5s ease, transform .5s ease;
        }
        .af-flow.in .af-stage-wrap { opacity: 1; transform: none; }
        .af-stage {
          flex: 1; min-width: 0; height: 100%;
          background: #ffffff; box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          border: 1px solid rgba(0,0,0,0.08); border-radius: 16px;
          padding: 22px 18px; text-align: center;
          display: flex; flex-direction: column; align-items: center; gap: 10px;
          transition: border-color .2s ease, background .2s ease, transform .2s ease;
        }
        .af-stage:hover { border-color: rgba(217,119,6,0.35); background: rgba(217,119,6,0.05); transform: translateY(-3px); }
        .af-stage[data-last="true"] { border-color: rgba(52,211,153,0.35); background: rgba(52,211,153,0.06); }
        .af-ico {
          width: 46px; height: 46px; border-radius: 50%; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; color: #d97706;
          background: rgba(217,119,6,0.12); border: 1px solid rgba(217,119,6,0.30);
        }
        .af-stage[data-last="true"] .af-ico { color: #34d399; background: rgba(52,211,153,0.14); border-color: rgba(52,211,153,0.35); }
        .af-stage-title { font-size: 16px; font-weight: 700; color: #111110; margin: 2px 0 0; }
        .af-stage-cap { font-size: 13px; line-height: 1.45; color: rgba(17,17,16,0.62); margin: 0; }
        .af-arrow {
          flex-shrink: 0; color: rgba(217,119,6,0.55);
          font-size: 28px; font-weight: 300; padding: 0 6px;
        }
        .af-closing {
          margin: clamp(32px, 5vw, 48px) auto 0; text-align: center;
          font-family: 'Fraunces', serif; font-style: italic; font-weight: 400;
          font-size: clamp(17px, 2.2vw, 22px); color: rgba(17,17,16,0.78);
        }
        @media (max-width: 880px) {
          .af-flow { flex-direction: column; align-items: stretch; max-width: 440px; margin-left: auto; margin-right: auto; }
          .af-stage-wrap { flex-direction: column; flex: none; }
          .af-stage { width: 100%; }
          .af-arrow { transform: rotate(90deg); padding: 8px 0; font-size: 24px; }
        }
      `}</style>
    </section>
  );
}
